import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import chartOfAccountsAPI from "../../api/chartOfAccountsAPI";
import accountTypesAPI from "../../api/accountTypesAPI";

const NewChartOfAccountPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accountTypes, setAccountTypes] = useState([]);
  const [parentAccounts, setParentAccounts] = useState([]);

  const [formData, setFormData] = useState({
    accountNumber: "",
    accountName: "",
    accountType: "",
    description: "",
    normalBalance: "debit",
    parentAccount: "",
    isActive: true,
    segment: "",
    statementSection: "",

    // ✅ new UI control
    autoNumber: true, // default ON
  });

  const isBillingTenant = localStorage.getItem('tenantType') === 'billing';

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [typesRes, accountsRes] = await Promise.all([
        accountTypesAPI.getAll({ activeOnly: true }),
        chartOfAccountsAPI.getAll({ activeOnly: true }),
      ]);

      const typesData =
        Array.isArray(typesRes) ? typesRes : typesRes?.data || typesRes?.data?.data || typesRes?.data || [];
      const accountsData =
        Array.isArray(accountsRes) ? accountsRes : accountsRes?.data || accountsRes?.data?.data || accountsRes?.data || [];

      setAccountTypes(Array.isArray(typesData) ? typesData : []);
      setParentAccounts(Array.isArray(accountsData) ? accountsData : []);
    } catch (err) {
      setError(err.message || "Failed to load initial data");
    } finally {
      setLoading(false);
    }
  };

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // toggle autoNumber
    if (name === "autoNumber") {
      const nextAuto = !!checked;
      setFormData((prev) => ({
        ...prev,
        autoNumber: nextAuto,
        // if turning auto back ON, clear so it can prefill
        accountNumber: nextAuto ? "" : prev.accountNumber,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const fetchNextNumber = async (accountTypeId) => {
    if (!accountTypeId) return;

    try {
      setPrefillLoading(true);
      setError(null);

      // ✅ IMPORTANT: assumes your chartOfAccountsAPI has a method for this.
      // If not, you can implement it easily (shown below).
      const res = await chartOfAccountsAPI.getNextNumber(accountTypeId);
      const nextNumber = res?.data?.nextNumber || res?.nextNumber || res?.data?.data?.nextNumber;

      if (nextNumber) {
        setField("accountNumber", nextNumber);
      }
    } catch (err) {
      // don’t block UX, but show message
      setError(err.message || "Failed to auto-generate next account number");
    } finally {
      setPrefillLoading(false);
    }
  };

  const handleAccountTypeChange = async (e) => {
    const accountTypeId = e.target.value;
    const selectedType = accountTypes.find((type) => type._id === accountTypeId);

    setFormData((prev) => ({
      ...prev,
      accountType: accountTypeId,
      normalBalance: selectedType?.normalBalance || "debit",
      // if autoNumber: clear number so it will prefill
      accountNumber: prev.autoNumber ? "" : prev.accountNumber,
    }));

    // ✅ prefill only when autoNumber ON
    if (formData.autoNumber) {
      await fetchNextNumber(accountTypeId);
    }
  };

  // If user toggles autoNumber ON after selecting a type, prefill again
  useEffect(() => {
    if (formData.autoNumber && formData.accountType && !formData.accountNumber) {
      fetchNextNumber(formData.accountType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.autoNumber]);

  // ✅ Frontend validation: check for duplicate account name (case-insensitive)
  const checkDuplicateAccountName = () => {
    const trimmedName = formData.accountName?.trim().toLowerCase();
    if (!trimmedName) return null;
    
    const duplicate = parentAccounts.find(
      (account) => account.accountName?.trim().toLowerCase() === trimmedName
    );
    
    return duplicate ? duplicate.accountName : null;
  };

  // ✅ Frontend validation: check for duplicate account code
  const checkDuplicateAccountCode = () => {
    if (!formData.autoNumber && !formData.accountNumber) return null;
    
    const accountNumToCheck = formData.autoNumber ? formData.accountNumber : formData.accountNumber;
    if (!accountNumToCheck) return null;
    
    const duplicate = parentAccounts.find(
      (account) => account.accountNumber === accountNumToCheck
    );
    
    return duplicate ? duplicate.accountNumber : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.accountName || !formData.accountType) {
      setError("Account Name and Account Type are required");
      return;
    }

    if (!formData.autoNumber && !formData.accountNumber) {
      setError("Account Number is required when Auto Number is OFF");
      return;
    }

    // ✅ Check for duplicate account name (case-insensitive)
    const duplicateName = checkDuplicateAccountName();
    if (duplicateName) {
      setError(`An account named "${duplicateName}" already exists. Account names must be unique (case-insensitive).`);
      return;
    }

    // ✅ Check for duplicate account code
    const duplicateCode = checkDuplicateAccountCode();
    if (duplicateCode) {
      setError(`Account number "${duplicateCode}" already exists. Account codes must be unique.`);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payload = {
        accountNumber: formData.autoNumber ? "" : formData.accountNumber,
        manualNumber: !formData.autoNumber, // ✅ backend uses this
        accountName: formData.accountName,
        accountType: formData.accountType,
        description: formData.description,
        normalBalance: formData.normalBalance,
        parentAccount: formData.parentAccount,
        isActive: formData.isActive,
        ...(showSegmentField ? { segment: formData.segment || null } : {}),
        ...(showStatementSectionField ? { statementSection: formData.statementSection || null } : {}),
      };

      const response = await chartOfAccountsAPI.create(payload);

      const accountId = response?._id || response?.data?._id || response?.data?.data?._id;
      if (accountId) {
        navigate(`/client/chart-of-accounts/`);
      } else {
        navigate(`/client/chart-of-accounts/`);
      }
    } catch (err) {
      setError(err.message || "Failed to create account");
    } finally {
      setLoading(false);
    }
  };

  const autoNumberDisabled = loading || prefillLoading || !formData.accountType;

  const selectedAccountTypeCategory = accountTypes.find((t) => t._id === formData.accountType)?.category;
  const showSegmentField = isBillingTenant && ["income", "expense"].includes(selectedAccountTypeCategory);
  const showStatementSectionField = ["income", "expense"].includes(selectedAccountTypeCategory);
  const isRevenueCategory = selectedAccountTypeCategory === "income";
  const statementSectionOptions = isRevenueCategory
    ? [
        { value: "", label: "Revenue" },
        { value: "OTHER_INCOME_EXPENSE", label: "Other income (expenses)" },
      ]
    : [
        { value: "", label: "Operating expense" },
        { value: "COST_OF_SERVICES", label: "Cost of services" },
        { value: "OTHER_INCOME_EXPENSE", label: "Other income (expenses)" },
      ];

  return (
    <Layout pageTitle="New Chart of Account">
      <div className="max-w-4xl mx-auto p-6 bg-gray-50 min-h-screen">
        <h1 className="text-2xl font-bold mb-6">New Chart of Account</h1>

        {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}

        {loading && (
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
            Loading data...
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Account Details</h2>

            {/* ✅ Auto number toggle */}
            <div className="flex items-center justify-between border rounded-lg p-4 mb-4 bg-gray-50">
              <div>
                <div className="font-semibold text-gray-800">Auto-generate Account Number</div>
                <div className="text-xs text-gray-500 mt-1">
                  Recommended. Prevents duplicate account numbers. You can switch to manual if needed.
                </div>
              </div>
              <input
                type="checkbox"
                name="autoNumber"
                checked={formData.autoNumber}
                onChange={handleChange}
                className="h-5 w-5 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Account Type *</label>
                <select
                  name="accountType"
                  value={formData.accountType}
                  onChange={handleAccountTypeChange}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                  disabled={loading || accountTypes.length === 0}
                >
                  <option value="">{loading ? "Loading..." : "Select Account Type"}</option>
                  {accountTypes?.map((type) => (
                    <option key={type._id} value={type._id}>
                      {type.name} ({type.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Normal Balance</label>
                <select
                  name="normalBalance"
                  value={formData.normalBalance}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  disabled={loading}
                >
                  <option value="debit">Debit</option>
                  <option value="credit">Credit</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Account Number *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={handleChange}
                    className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      formData.autoNumber ? "bg-gray-100" : ""
                    } ${
                      checkDuplicateAccountCode() ? "border-red-500 bg-red-50" : ""
                    }`}
                    placeholder={formData.autoNumber ? "Auto-generated" : "e.g., 1000"}
                    disabled={loading || (formData.autoNumber && true)}
                    required={!formData.autoNumber}
                  />

                  {formData.autoNumber && (
                    <button
                      type="button"
                      onClick={() => fetchNextNumber(formData.accountType)}
                      className="px-3 py-2 border rounded-md text-sm text-gray-700 hover:bg-gray-50"
                      disabled={autoNumberDisabled}
                      title="Refresh next number"
                    >
                      {prefillLoading ? "..." : "Refresh"}
                    </button>
                  )}
                </div>

                {checkDuplicateAccountCode() && (
                  <p className="text-xs text-red-600 mt-1 font-semibold">
                    ✕ Account number "{checkDuplicateAccountCode()}" already exists
                  </p>
                )}

                <p className="text-xs text-gray-500 mt-1">
                  {formData.autoNumber
                    ? "We will assign the next available number for this account type."
                    : "Manual number. Make sure it matches your numbering system."}
                </p>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Account Name *</label>
                <input
                  type="text"
                  name="accountName"
                  value={formData.accountName}
                  onChange={handleChange}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                    checkDuplicateAccountName() ? "border-red-500 bg-red-50" : ""
                  }`}
                  placeholder="e.g., Cash on Hand"
                  required
                  disabled={loading}
                />
                {checkDuplicateAccountName() && (
                  <p className="text-xs text-red-600 mt-1 font-semibold">
                    ✕ An account with this name already exists (case-insensitive)
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Parent Account</label>
                <select
                  name="parentAccount"
                  value={formData.parentAccount}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  disabled={loading || parentAccounts.length === 0}
                >
                  <option value="">{loading ? "Loading..." : "No Parent Account"}</option>
                  {parentAccounts?.map((account) => (
                    <option key={account._id} value={account._id}>
                      {account.accountNumber} - {account.accountName}
                    </option>
                  ))}
                </select>
              </div>

              {showSegmentField && (
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Segment</label>
                  <select
                    name="segment"
                    value={formData.segment || ""}
                    onChange={handleChange}
                    className="w-full border border-ink-line rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    disabled={loading}
                  >
                    <option value="">Standard</option>
                    <option value="TRUCKING">Trucking</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Reporting-layer classification for income statement segmentation. Does not affect GL postings.
                  </p>
                </div>
              )}

              {showStatementSectionField && (
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Statement section</label>
                  <select
                    name="statementSection"
                    value={formData.statementSection || ""}
                    onChange={handleChange}
                    className="w-full border border-ink-line rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    disabled={loading}
                  >
                    {statementSectionOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Which income statement section this account rolls up into.
                  </p>
                </div>
              )}

              <div className="flex items-center mt-6">
                <input
                  type="checkbox"
                  name="isActive"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  disabled={loading}
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                  Active Account
                </label>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                rows={3}
                placeholder="Optional description..."
                disabled={loading}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/client/chart-of-accounts")}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={loading || accountTypes.length === 0 || checkDuplicateAccountName() || checkDuplicateAccountCode()}
            >
              {loading ? "Creating..." : "Create Account"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default NewChartOfAccountPage;
